import { DiscussionContextType, Message, QUERY_KEYS } from "@cnx/common";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import MessageService from "services/MessageService";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import { getQueryKeysForDiscussionContext } from "utils/chat";

type UseReadMessagesSocketParams = {
  messages: Message[];
  discussionId?: string;
  context?: DiscussionContextType;
};

export const useReadMessagesSocket = ({
  messages,
  discussionId,
  context,
}: UseReadMessagesSocketParams) => {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore(state => state.currentUserId);
  const socket = useSocket();

  useEffect(() => {
    if (!messages.length || !discussionId || !currentUserId || !socket) return;

    const readMessages = async () => {
      let recipientRead = false;

      for (const message of messages) {
        const recipient = message.recipients?.find(
          recipient => recipient.userId === currentUserId
        );

        if (recipient && !recipient.read) {
          await MessageService.markMessageAsRead(message.id ?? "");
          recipientRead = true;

          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.MESSAGES, discussionId],
          });

          socket.emit("read_message", { discussionId });
        }
      }

      if (recipientRead && context) {
        const contextQueryKeys = getQueryKeysForDiscussionContext(context);
        if (contextQueryKeys) {
          await Promise.all(
            contextQueryKeys.map(queryKey =>
              queryClient.invalidateQueries({ queryKey })
            )
          );
        }
      }
    };

    readMessages();
  }, [messages, discussionId, currentUserId, queryClient, socket, context]);
};
